'use client';

import { showNotification } from '@commenda/kakashi';
import type {
  ComponentVisibility,
  DSLFile,
  FormDSL,
  FormModule,
  FormStep,
  SubFormFieldBinding,
  ValidationRules,
} from '@commenda/types';
import { FormsTemplateType } from '@commenda/types';
import type { DSLContext } from '@commenda/types/src/commenda-os-dsl';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormContext, type FormContextType } from '../../hooks/FormContext';
import {
  useCommendaOSPrefill,
  useCommendaOSSubmit,
} from '../../hooks/useCommendaOSIntegration';
import { PreviewTestingPanel } from '../../preview/PreviewTestingPanel';
import { useLinkedFormPreviewTesting } from '../../preview/useLinkedFormPreviewTesting';
import { CommendaOSFailureHelper } from '../../utils/commenda-os-failure-helper';
import {
  buildComponentMap,
  buildFieldLabelMap,
} from '../../utils/dsl-traversal';
import {
  findModuleIdForField,
  getDefaultExpandedModulesForStep,
  isInstanceFieldVisible,
  isValueEmpty,
  rewriteRulesForInstance,
} from '../../utils/form-state';
import { LinkedFormDslHelper } from '../../utils/linked-form-dsl-helper';
import { fieldValidationError } from '../../utils/validation';
import {
  CollapsibleModuleCard,
  NewIncorporationLayout,
  ProgressStepItem,
} from '../new-incorp-layout';
import { LinkedFormSheetLayout } from './LinkedFormSheetLayout';
import { ModuleRenderer } from './ModuleRenderer';
import { RepeatableModuleRenderer } from './RepeatableModuleRenderer';
import { StepRenderer } from './StepRenderer';

/** Layout settings that can be passed to LinkedFormRenderer to override DSL settings */
export interface LinkedFormLayoutSettings {
  hideSidebar?: boolean;
  hideHeader?: boolean;
  hideTopHeader?: boolean;
  hideActions?: boolean;
  hideCloseButton?: boolean;
  disableNavigation?: boolean;
}

/**
 * Stable identities for the optional record props: an inline `{}` default would
 * be a new object every render and retrigger the effects keyed on them.
 */
const EMPTY_VALUES: Record<string, unknown> = {};

// A field takes the whole row unless the form says otherwise, so pairing or tripling short fields
// is opt-in and nothing reflows without being asked. Narrow screens stay one per row.
const FIELD_WIDTH_SPAN = {
  full: 'col-span-6',
  half: 'col-span-6 sm:col-span-3',
  third: 'col-span-6 sm:col-span-2',
} as const;

const fieldWidthSpan = (width?: string): string =>
  FIELD_WIDTH_SPAN[width as keyof typeof FIELD_WIDTH_SPAN] ??
  FIELD_WIDTH_SPAN.full;

export const LinkedFormRenderer = ({
  dsl,
  type,
  defaultValues = EMPTY_VALUES,
  parentValuesForValidation = EMPTY_VALUES,
  fieldBindings = [],
  preventSameValueForAllBindings = false,
  globalSameValueErrorMessage,
  showSidebar = false,
  onStepChange,
  layoutSettings,
  onClose,
  onSubmit,
  title: titleOverride,
  subtitle: subtitleOverride,
  embedMode = false,
  registerSubmit,
  componentRegistry,
}: {
  dsl: FormDSL;
  type: FormsTemplateType;
  defaultValues?: Record<string, unknown>;
  parentValuesForValidation?: Record<string, unknown>;
  fieldBindings?: SubFormFieldBinding[];
  preventSameValueForAllBindings?: boolean;
  globalSameValueErrorMessage?: string;
  showSidebar?: boolean;
  onStepChange?: (stepIndex: number, totalSteps: number) => void;
  /** Optional layout settings to override DSL settings */
  layoutSettings?: LinkedFormLayoutSettings;
  /** Callback when the form is closed */
  onClose?: () => void;
  /** Callback when the form is submitted, receives the subform data */
  onSubmit?: (data: Record<string, unknown>) => void;
  /** Override the form title from DSL */
  title?: string;
  /** Override the form subtitle/description from DSL */
  subtitle?: string;
  /** When true, do not wrap modules in CollapsibleModuleCard (for nested form content) */
  embedMode?: boolean;
  /**
   * Hands the validated submit to the host, so one rendering this with
   * `layoutSettings.hideActions` can drive it from its own footer rather than
   * showing a second set of buttons. Called with null on unmount.
   */
  registerSubmit?: (submit: (() => void) | null) => void;
  componentRegistry: Record<string, React.FC<any>>;
}) => {
  // Create local state for the subform, initialized with default values
  const [subformData, setSubformData] =
    useState<Record<string, unknown>>(defaultValues);
  const [subformErrors, setSubformErrors] = useState<Record<string, string>>(
    {}
  );
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isLinkedFormSubmitting, setIsLinkedFormSubmitting] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(() => {
    const linkedFormDsl = dsl as unknown as DSLFile;
    const initialStep = [...(linkedFormDsl?.steps ?? [])].sort(
      (a, b) => a.order - b.order
    )[0];
    return getDefaultExpandedModulesForStep(initialStep);
  });

  // Handle module expand/collapse
  const handleModuleExpandChange = useCallback(
    (moduleId: string, expanded: boolean) => {
      setExpandedModules((prev) => {
        const newSet = new Set(prev);
        if (expanded) {
          newSet.add(moduleId);
        } else {
          newSet.delete(moduleId);
        }
        return newSet;
      });
    },
    []
  );

  // Update subform data when defaultValues change (e.g., when parent form field changes)
  useEffect(() => {
    setSubformData((prev) => ({ ...prev, ...defaultValues }));
  }, [defaultValues]);

  const updateSubformField = useCallback(
    (fieldName: string, value: unknown) => {
      setSubformData((prev) => ({ ...prev, [fieldName]: value }));
    },
    []
  );

  // Get the binding config for a field (if any)
  const getBindingForField = useCallback(
    (fieldName: string): SubFormFieldBinding | undefined => {
      return fieldBindings.find((b) => b.subformFieldName === fieldName);
    },
    [fieldBindings]
  );

  // Handle step navigation
  const handleStepChange = useCallback(
    (newStepIndex: number) => {
      // Mark current step as completed when moving forward
      if (newStepIndex > currentStepIndex) {
        setCompletedSteps(
          (prev) => new Set(Array.from(prev).concat(currentStepIndex))
        );
      }
      setCurrentStepIndex(newStepIndex);
      onStepChange?.(
        newStepIndex,
        (dsl as unknown as DSLFile)?.steps?.length || 0
      );
    },
    [currentStepIndex, dsl, onStepChange]
  );

  // Inherit dslContext from parent so linked forms (e.g. per-key-person forms) can prefill
  const parentContext = useContext(FormContext);

  // Stable callback ref to avoid re-triggering prefill effects when subformData changes
  const onPrefillCompleteRef =
    useRef<(data: Record<string, unknown>) => void>();
  onPrefillCompleteRef.current = (prefillData: Record<string, unknown>) => {
    setSubformData((prev) => ({
      ...prefillData,
      ...prev,
    }));
  };
  const stableOnPrefillComplete = useCallback(
    (data: Record<string, unknown>) => onPrefillCompleteRef.current?.(data),
    []
  );

  const normalizedDsl = useMemo(
    (): FormDSL => LinkedFormDslHelper.normalize(dsl, type),
    [dsl, type]
  );

  const subformComponentMap = useMemo(
    () => buildComponentMap(normalizedDsl as unknown as DSLFile),
    [normalizedDsl]
  );

  // A `form-field` option source names the field whose entries become the options, and
  // in a linked form that field is usually one of the host's — an owner's representative
  // is picked from the directors the host form collected. The sub-form keeps its own
  // data, so those ids resolve to nothing and the list renders empty. Borrow the host's
  // value for exactly the ids such a source names, and only where the linked form has no
  // field of its own by that id. Edits and the submitted payload still come from
  // `subformData` alone, so nothing borrowed is written back.
  const borrowedHostFields = useMemo(() => {
    const own = new Set<string>();
    const wanted = new Set<string>();
    normalizedDsl.steps?.forEach((step) =>
      step.modules?.forEach((module) =>
        module.components?.forEach((component) => {
          own.add(component.fieldId);
          const source = component.dataSource?.formField;
          if (source)
            [source.moduleId, ...(source.alsoFrom ?? [])].forEach((id) => {
              if (id) wanted.add(id);
            });
        })
      )
    );
    const borrowed: Record<string, unknown> = {};
    wanted.forEach((id) => {
      if (own.has(id)) return;
      const value = parentContext.formData?.[id];
      if (value !== undefined) borrowed[id] = value;
    });
    return borrowed;
  }, [normalizedDsl, parentContext.formData]);

  const isSubformFieldVisible = useCallback(
    (visibility?: ComponentVisibility): boolean =>
      LinkedFormDslHelper.isVisible(
        visibility,
        subformData,
        subformComponentMap
      ),
    [subformData, subformComponentMap]
  );

  const validateSubformField = useCallback(
    (
      fieldName: string,
      value: unknown,
      rules?: ValidationRules
    ): string | null => {
      const error = fieldValidationError(value, rules, {
        isFieldVisible: isSubformFieldVisible,
      });
      if (error) {
        setSubformErrors((prev) => ({ ...prev, [fieldName]: error }));
        return error;
      }

      // Not a field rule: it compares against the form that opened this one.
      const binding = getBindingForField(fieldName);
      const parentValue = parentValuesForValidation[fieldName];
      const shouldPreventSameValue =
        preventSameValueForAllBindings || (binding?.preventSameValue ?? false);

      if (shouldPreventSameValue && parentValue !== undefined) {
        const valuesAreSame =
          value === parentValue ||
          (typeof value === 'string' &&
            typeof parentValue === 'string' &&
            value.toLowerCase() === parentValue.toLowerCase());

        if (value && valuesAreSame) {
          const message =
            binding?.sameValueErrorMessage ||
            globalSameValueErrorMessage ||
            'This value cannot be the same as the parent field';
          setSubformErrors((prev) => ({ ...prev, [fieldName]: message }));
          return message;
        }
      }

      setSubformErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      return null;
    },
    [
      isSubformFieldVisible,
      getBindingForField,
      parentValuesForValidation,
      preventSameValueForAllBindings,
      globalSameValueErrorMessage,
    ]
  );

  // Prefill subform fields from Commenda OS DSL (e.g. for existing key persons).
  // Pass an empty object as formData so the prefill effects don't re-fire on every
  // subformData change — the linked form has no context-bound fields that depend on
  // other form values.
  const emptyFormData = useRef<Record<string, unknown>>({}).current;
  const { isLoading: isLinkedFormPrefilling } = useCommendaOSPrefill({
    dsl: normalizedDsl,
    dslContext: parentContext.dslContext as DSLContext,
    formData: emptyFormData,
    onPrefillComplete: stableOnPrefillComplete,
  });

  // Commenda OS submit integration for the linked form
  const { submit: submitLinkedFormToCommendaOS } = useCommendaOSSubmit({
    dsl: normalizedDsl,
    dslContext: parentContext.dslContext as DSLContext,
    formData: subformData,
  });

  const runLinkedFormCosAndParentSubmit = useCallback(async () => {
    if (isLinkedFormSubmitting) return;
    // Preview never writes. The host still gets the entered values so its row
    // shows them, as the paged form does with onSubmitSuccess('preview').
    if (parentContext.isPreview) {
      onSubmit?.({ ...subformData });
      return;
    }
    setIsLinkedFormSubmitting(true);
    let cosResult:
      | {
          success: boolean;
          results: Array<{
            entity: string;
            success: boolean;
            id?: number | string;
            error?: string;
            code?: string;
          }>;
        }
      | undefined;
    let cosError: unknown;
    try {
      cosResult = await submitLinkedFormToCommendaOS();
    } catch (err) {
      cosError = err;
      console.error('[LinkedFormRenderer] Commenda OS submit failed:', err);
    } finally {
      setIsLinkedFormSubmitting(false);
    }

    // Handing the parent a submit the write never backed closes the drawer and adds
    // a row for a person or entity that was not created. The paged form already
    // reports its failed writes instead of completing on top of them; this did not,
    // so the only sign was a console line.
    if (cosError || cosResult?.success === false) {
      const failed =
        cosResult?.results?.filter((result) => !result.success) ?? [];
      showNotification({
        title: 'Details were not saved',
        message: cosError
          ? `${CommendaOSFailureHelper.describeHandlerRejections([{ reason: cosError }])} Please try again.`
          : `${CommendaOSFailureHelper.describeCommendaOSFailures(failed)} Please review and submit again.`,
        valid: false,
      });
      return;
    }

    const dataToSubmit = { ...subformData };
    const kpResult = cosResult?.results.find(
      (r) => r.entity === 'keyPerson' && r.success && r.id
    );
    if (kpResult?.id) {
      dataToSubmit.__createdDirectorId = kpResult.id;
    }

    onSubmit?.(dataToSubmit);
  }, [
    isLinkedFormSubmitting,
    parentContext.isPreview,
    submitLinkedFormToCommendaOS,
    subformData,
    onSubmit,
  ]);

  // A repeatable module stores its instances as an array under `saveKey`, so its
  // fields live at `saveKey.<index>.<fieldId>` rather than at the top level.
  // Validating them by bare fieldId would read `undefined` and fail every
  // required check, so each instance is validated with scoped ids and rules.
  const validateRepeatableModule = useCallback(
    (module: FormModule): string | undefined => {
      const saveKey = module.saveKey || module.id;
      const instances = subformData[saveKey];
      const instancesArray = Array.isArray(instances)
        ? (instances as Record<string, unknown>[])
        : [];
      let firstErrorField: string | undefined;

      instancesArray.forEach((instanceData, instanceIndex) => {
        const prefix = `${saveKey}.${instanceIndex}.`;
        module.components?.forEach((component) => {
          if (!component.fieldId) return;
          if (component.component === 'FormSummary') return;
          if (
            !isInstanceFieldVisible(
              component.visibility,
              instanceData ?? {},
              isSubformFieldVisible,
              subformComponentMap
            )
          ) {
            return;
          }

          const scopedFieldId = `${prefix}${component.fieldId}`;
          const error = validateSubformField(
            scopedFieldId,
            (instanceData ?? {})[component.fieldId],
            rewriteRulesForInstance(component.validation?.rules, prefix)
          );
          if (error && !firstErrorField) firstErrorField = scopedFieldId;
        });
      });

      return firstErrorField;
    },
    [
      subformData,
      subformComponentMap,
      isSubformFieldVisible,
      validateSubformField,
    ]
  );

  const validateLinkedFormSteps = useCallback(
    (steps: FormStep[]): { isValid: boolean; firstErrorField?: string } => {
      let hasErrors = false;
      let firstErrorField: string | undefined;

      steps.forEach((step) => {
        // Skip hidden steps entirely — their components are not rendered, so
        // their values cannot be filled and required validation would always
        // fail silently with no UI to surface the errors.
        if (!isSubformFieldVisible(step.visibility)) return;
        step.modules?.forEach((module) => {
          // Same for hidden modules: their components are not rendered, so
          // required validation on those components blocks submit with
          // invisible errors. Respect module-level visibility before
          // descending into children.
          if (!isSubformFieldVisible(module.visibility)) return;

          if (module.repeatable?.enabled) {
            const repeatableError = validateRepeatableModule(module);
            if (repeatableError) {
              hasErrors = true;
              if (!firstErrorField) firstErrorField = repeatableError;
            }
            return;
          }

          module.components?.forEach((component) => {
            if (!isSubformFieldVisible(component.visibility)) return;
            if (!component.fieldId) return;
            if (component.component === 'FormSummary') return;

            if (component.component === 'NestedForm') {
              const entries = subformData[component.fieldId];
              const count = Array.isArray(entries) ? entries.length : 0;
              const minProp = component.props?.min;
              const maxProp = component.props?.max;
              const minCount =
                minProp != null && minProp !== '' ? Number(minProp) : undefined;
              const maxCount =
                maxProp != null && maxProp !== '' ? Number(maxProp) : undefined;
              if (minCount != null && minCount > 0 && count < minCount) {
                const msg = `Please add at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
                setSubformErrors((prev) => ({
                  ...prev,
                  [component.fieldId]: msg,
                }));
                hasErrors = true;
                if (!firstErrorField) firstErrorField = component.fieldId;
                return;
              }
              if (maxCount != null && count > maxCount) {
                const msg = `Please add at most ${maxCount} entr${maxCount === 1 ? 'y' : 'ies'}`;
                setSubformErrors((prev) => ({
                  ...prev,
                  [component.fieldId]: msg,
                }));
                hasErrors = true;
                if (!firstErrorField) firstErrorField = component.fieldId;
                return;
              }
              setSubformErrors((prev) => {
                const next = { ...prev };
                delete next[component.fieldId];
                return next;
              });
              return;
            }

            const value = subformData[component.fieldId];
            const error = validateSubformField(
              component.fieldId,
              value,
              component.validation?.rules
            );
            if (error) {
              hasErrors = true;
              if (!firstErrorField) firstErrorField = component.fieldId;
            }
          });
        });
      });

      return { isValid: !hasErrors, firstErrorField };
    },
    [
      isSubformFieldVisible,
      subformData,
      validateSubformField,
      validateRepeatableModule,
      setSubformErrors,
    ]
  );

  const handleEmbeddedLinkedFormSubmit = useCallback(async () => {
    const steps = normalizedDsl.steps ?? [];
    const { isValid, firstErrorField } = validateLinkedFormSteps(steps);
    if (!isValid) {
      // A collapsed module is unmounted, so its error text never renders and
      // the submit looks like it did nothing. Open the offending section and
      // scroll to the field, the same way the paged form does.
      const owner = steps.find(
        (step) => findModuleIdForField(step, firstErrorField) !== undefined
      );
      const moduleId = findModuleIdForField(owner, firstErrorField);
      if (moduleId) setExpandedModules((prev) => new Set(prev).add(moduleId));
      if (firstErrorField && typeof document !== 'undefined') {
        setTimeout(() => {
          document
            .querySelector(`[data-field-id="${firstErrorField}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
      return;
    }
    await runLinkedFormCosAndParentSubmit();
  }, [normalizedDsl, validateLinkedFormSteps, runLinkedFormCosAndParentSubmit]);

  useEffect(() => {
    if (!registerSubmit) return undefined;
    registerSubmit(handleEmbeddedLinkedFormSubmit);
    return () => registerSubmit(null);
  }, [registerSubmit, handleEmbeddedLinkedFormSubmit]);

  // Create a subform context value
  const { registerPreviewFillHandler, panelProps: previewPanelProps } =
    useLinkedFormPreviewTesting({
      isPreview: Boolean(parentContext.isPreview),
      steps: normalizedDsl.steps ?? [],
      currentStep: currentStepIndex,
      setCurrentStep: handleStepChange,
      formData: subformData,
      dslContext: parentContext.dslContext,
      updateField: updateSubformField,
      validateField: validateSubformField,
      isFieldVisible: isSubformFieldVisible,
      validateSteps: validateLinkedFormSteps,
      componentMap: subformComponentMap,
      errors: subformErrors,
      setErrors: setSubformErrors,
      setExpandedModules,
      isCommendaOSPrefilling: isLinkedFormPrefilling,
    });

  const subformContextValue: FormContextType = useMemo(
    () => ({
      formData: { ...borrowedHostFields, ...subformData },
      setFormData: setSubformData,
      updateField: updateSubformField,
      currentStep: currentStepIndex,
      setCurrentStep: handleStepChange,
      errors: subformErrors,
      setErrors: setSubformErrors,
      validateField: validateSubformField,
      isFieldVisible: isSubformFieldVisible,
      isFieldDisabled: () => false,
      componentMap: subformComponentMap,
      dslContext: parentContext.dslContext,
      nestedFormOpen: null,
      setNestedFormOpen: () => {},
      uploadingFields: new Set<string>(),
      setFieldUploading: () => {},
      isPreview: parentContext.isPreview,
      registerPreviewFillHandler,
    }),
    [
      subformData,
      borrowedHostFields,
      subformErrors,
      updateSubformField,
      isSubformFieldVisible,
      validateSubformField,
      subformComponentMap,
      currentStepIndex,
      parentContext.dslContext,
      handleStepChange,
      parentContext.isPreview,
      registerPreviewFillHandler,
    ]
  );

  const previewTestingPanel = previewPanelProps ? (
    <PreviewTestingPanel {...previewPanelProps} />
  ) : null;

  // Handle different types
  if (type === FormsTemplateType.FORM_MODULE) {
    // DSL is a single module — normalize DSL for COS hooks above; add actions so drawer can submit.
    const module = dsl as unknown as FormModule;
    return (
      <FormContext.Provider value={subformContextValue}>
        {previewTestingPanel}
        <LinkedFormSheetLayout
          title={module.name}
          hideHeader={layoutSettings?.hideHeader}
          hideActions={!onSubmit || layoutSettings?.hideActions}
          isSubmitting={isLinkedFormSubmitting}
          onCancel={onClose}
          onSubmit={() => handleEmbeddedLinkedFormSubmit()}
        >
          <ModuleRenderer
            module={module}
            componentRegistry={componentRegistry}
            hideName={!layoutSettings?.hideHeader}
          />
        </LinkedFormSheetLayout>
      </FormContext.Provider>
    );
  }

  if (type === FormsTemplateType.FORM_STEP) {
    // DSL is a single step
    const step = dsl as unknown as FormStep;
    return (
      <FormContext.Provider value={subformContextValue}>
        {previewTestingPanel}
        <LinkedFormSheetLayout
          title={step.title}
          hideHeader={layoutSettings?.hideHeader}
          hideActions={!onSubmit || layoutSettings?.hideActions}
          isSubmitting={isLinkedFormSubmitting}
          onCancel={onClose}
          onSubmit={() => handleEmbeddedLinkedFormSubmit()}
        >
          <StepRenderer step={step} componentRegistry={componentRegistry} />
        </LinkedFormSheetLayout>
      </FormContext.Provider>
    );
  }

  // FORM_TEMPLATE - DSL is a full form
  const formDsl = dsl as unknown as DSLFile;
  if (!formDsl?.steps?.length) {
    return (
      <div className="text-center py-8">
        <p className="txt-small-regular text-text-tertiary">
          This form template has no steps configured.
        </p>
      </div>
    );
  }

  // Extract layout settings from DSL and merge with overrides
  const dslSettings = formDsl?.form?.settings ?? {};
  const effectiveLayoutSettings: LinkedFormLayoutSettings = {
    hideSidebar: layoutSettings?.hideSidebar ?? false,
    hideHeader: layoutSettings?.hideHeader ?? false,
    hideTopHeader: layoutSettings?.hideTopHeader ?? false,
    hideActions: layoutSettings?.hideActions ?? false,
    hideCloseButton: layoutSettings?.hideCloseButton ?? false,
    disableNavigation: layoutSettings?.disableNavigation ?? false,
  };

  // Sort steps by order
  const sortedSteps = [...formDsl.steps].sort((a, b) => a.order - b.order);
  const currentStepData = sortedSteps[currentStepIndex];

  // Submit is disabled while this step cannot pass the validation it runs on click, so a drawer
  // missing something required reads as blocking instead of failing when pressed. Repeatable
  // modules keep entries under a save key rather than flat field ids and validate separately.
  // Not a hook: this sits after the early returns above, and a plain pass over the current step's
  // components is cheap enough not to need memoising.
  const currentSubformStepBlocked = !currentStepData
    ? false
    : (currentStepData.modules ?? []).some((module) => {
        if (module.repeatable?.enabled) return false;
        if (!isSubformFieldVisible(module.visibility)) return false;

        return (module.components ?? []).some((component) => {
          if (!isSubformFieldVisible(component.visibility)) return false;
          const { fieldId } = component;
          if (!fieldId || component.component === 'FormSummary') return false;
          if (subformErrors[fieldId]) return true;

          const rules = (
            component.validation as { rules?: ValidationRules } | undefined
          )?.rules;
          const required = rules?.required;
          const isRequired =
            typeof required === 'object' ? required.enabled : !!required;
          return !!isRequired && isValueEmpty(subformData[fieldId]);
        });
      });
  const isLastStep = currentStepIndex === sortedSteps.length - 1;
  const isMultiStep = sortedSteps.length > 1;

  // Reset expanded modules when the linked form step changes.
  // We intentionally avoid re-seeding on every visibility-data change.
  const prevLinkedStepRef = useRef(currentStepIndex);
  useEffect(() => {
    if (prevLinkedStepRef.current === currentStepIndex) return;
    prevLinkedStepRef.current = currentStepIndex;

    const stepData = sortedSteps[currentStepIndex];
    setExpandedModules(
      getDefaultExpandedModulesForStep(stepData, (module) =>
        isSubformFieldVisible(module.visibility as ComponentVisibility)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, normalizedDsl?.steps]);

  // Build sidebar steps for multi-step forms
  const progressSteps: ProgressStepItem[] = sortedSteps.map((step, index) => ({
    id: step.id,
    text: step.title || `Step ${index + 1}`,
    isCompleted: completedSteps.has(index),
    disabled: false,
    isHidden: false,
  }));

  // Form title and subtitle
  const formTitle = titleOverride ?? formDsl?.form?.title ?? 'Form';
  const formSubtitle =
    subtitleOverride ??
    ((dslSettings as Record<string, unknown>)?.description as
      | string
      | undefined);

  // A collapsed module is unmounted, so its error text never renders and the
  // scroll below finds nothing — open the offending section first, then scroll.
  const revealFieldWithError = (
    step: FormStep | undefined,
    fieldId: string | undefined
  ) => {
    const moduleId = findModuleIdForField(step, fieldId);
    if (moduleId) {
      setExpandedModules((prev) => new Set(prev).add(moduleId));
    }
    if (!fieldId || typeof document === 'undefined') return;
    // Let the newly expanded module mount before looking for the field.
    setTimeout(() => {
      document
        .querySelector(`[data-field-id="${fieldId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Validate all visible fields in the current step before allowing next/submit
  const validateCurrentSubformStep = (): {
    isValid: boolean;
    firstErrorField?: string;
  } => {
    let hasErrors = false;
    let firstErrorField: string | undefined;
    const step = currentStepData;
    if (!step) return { isValid: true };

    step.modules?.forEach((module) => {
      // A hidden module is not rendered, so its required fields can never be
      // filled and never show their errors — validating them blocks submit with
      // nothing on screen to explain why. validateLinkedFormSteps already skips
      // them; this path did not.
      if (!isSubformFieldVisible(module.visibility)) return;

      if (module.repeatable?.enabled) {
        const repeatableError = validateRepeatableModule(module);
        if (repeatableError) {
          hasErrors = true;
          if (!firstErrorField) firstErrorField = repeatableError;
        }
        return;
      }

      module.components?.forEach((component) => {
        if (!isSubformFieldVisible(component.visibility)) return;
        if (!component.fieldId) return;
        if (component.component === 'FormSummary') return;

        // NestedForm min/max validation (subform in drawer)
        if (component.component === 'NestedForm') {
          const entries = subformData[component.fieldId];
          const count = Array.isArray(entries) ? entries.length : 0;
          const minProp = component.props?.min;
          const maxProp = component.props?.max;
          const minCount =
            minProp != null && minProp !== '' ? Number(minProp) : undefined;
          const maxCount =
            maxProp != null && maxProp !== '' ? Number(maxProp) : undefined;
          if (minCount != null && minCount > 0 && count < minCount) {
            const msg = `Please add at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
            setSubformErrors((prev) => ({ ...prev, [component.fieldId]: msg }));
            hasErrors = true;
            if (!firstErrorField) firstErrorField = component.fieldId;
            return;
          }
          if (maxCount != null && count > maxCount) {
            const msg = `Please add at most ${maxCount} entr${maxCount === 1 ? 'y' : 'ies'}`;
            setSubformErrors((prev) => ({ ...prev, [component.fieldId]: msg }));
            hasErrors = true;
            if (!firstErrorField) firstErrorField = component.fieldId;
            return;
          }
          setSubformErrors((prev) => {
            const next = { ...prev };
            delete next[component.fieldId];
            return next;
          });
          return;
        }

        const value = subformData[component.fieldId];
        const error = validateSubformField(
          component.fieldId,
          value,
          component.validation?.rules
        );
        if (error) {
          hasErrors = true;
          if (!firstErrorField) firstErrorField = component.fieldId;
        }
      });
    });

    return { isValid: !hasErrors, firstErrorField };
  };

  // Handle next step or submit
  const handleNext = async () => {
    const { isValid, firstErrorField } = validateCurrentSubformStep();
    if (!isValid) {
      revealFieldWithError(currentStepData, firstErrorField);
      return;
    }
    if (isLastStep) {
      await runLinkedFormCosAndParentSubmit();
    } else {
      handleStepChange(currentStepIndex + 1);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStepIndex > 0) {
      handleStepChange(currentStepIndex - 1);
    }
  };

  // Use IncorporationFormLayout for FORM_TEMPLATE rendering
  return (
    <FormContext.Provider value={subformContextValue}>
      {previewTestingPanel}
      <NewIncorporationLayout
        title={formTitle}
        subtitle={formSubtitle}
        steps={progressSteps}
        activeStepIndex={currentStepIndex}
        onStepClick={handleStepChange}
        hideSidebar={effectiveLayoutSettings.hideSidebar || !isMultiStep}
        hideHeader={effectiveLayoutSettings.hideHeader}
        hideTopHeader={effectiveLayoutSettings.hideTopHeader}
        hideActions={effectiveLayoutSettings.hideActions}
        hideCloseButton={effectiveLayoutSettings.hideCloseButton}
        disableNavigation={effectiveLayoutSettings.disableNavigation}
        isEmbedded={embedMode}
        header={
          !effectiveLayoutSettings.hideTopHeader && onClose
            ? {
                onClose,
                hideCloseButton: effectiveLayoutSettings.hideCloseButton,
              }
            : undefined
        }
        formActions={
          !effectiveLayoutSettings.hideActions
            ? {
                onBack: currentStepIndex > 0 ? handleBack : undefined,
                onNext: handleNext,
                onCancel: onClose,
                backLabel: 'Back',
                nextLabel: isLastStep ? 'Submit' : 'Next',
                disableBack: currentStepIndex === 0,
                hideBack: currentStepIndex === 0,
                showCancel: currentStepIndex === 0 && !!onClose,
                disableNext:
                  isLinkedFormSubmitting || currentSubformStepBlocked,
                isLoading: isLinkedFormSubmitting,
              }
            : undefined
        }
      >
        {currentStepData?.modules?.map((module) => {
          if (
            module.visibility &&
            !isSubformFieldVisible(module.visibility as ComponentVisibility)
          ) {
            return null;
          }

          // Repeatable modules own their instance list and add/remove controls,
          // so they must go through RepeatableModuleRenderer instead of the
          // flat component map below (which renders one instance, no add button).
          if (module.repeatable?.enabled) {
            // Carded like every other module, embedded or not. Without this the rows
            // and their add button sit loose among the cards around them, with no
            // heading saying what the list is or where it ends.
            return (
              <CollapsibleModuleCard
                key={module.id}
                title={module.name}
                description={module.description}
                isExpanded={expandedModules.has(module.id)}
                onExpandedChange={(expanded) =>
                  handleModuleExpandChange(module.id, expanded)
                }
              >
                <RepeatableModuleRenderer
                  module={module}
                  componentRegistry={componentRegistry}
                  parentContext={subformContextValue}
                  isFieldVisible={isSubformFieldVisible}
                />
              </CollapsibleModuleCard>
            );
          }

          const visibleComponents = module.components
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .filter((component) => isSubformFieldVisible(component.visibility));
          // Only skip section wrapper for NestedForm-only modules when in embed mode
          const nestedFormsInModule = visibleComponents.filter(
            (c) => c.component === 'NestedForm'
          );
          const otherWithFieldId = visibleComponents.filter(
            (c) => c.fieldId && c.component !== 'NestedForm'
          );
          const isNestedFormOnlyModule =
            embedMode &&
            nestedFormsInModule.length === 1 &&
            otherWithFieldId.length === 0;

          const componentsContent = visibleComponents.map(
            (component, index) => {
              const Component = componentRegistry[component.component];
              if (!Component) {
                return null;
              }

              if (component.component === 'FormSummary') {
                const summaryModules = formDsl?.steps?.flatMap((step) =>
                  step.modules.map((m) => ({
                    id: m.id,
                    name: m.name,
                    description: m.description,
                    saveKey: m.saveKey,
                    repeatable: (m.repeatable as { enabled?: boolean })
                      ?.enabled,
                    fieldIds: m.components
                      .map((c) => c.fieldId)
                      .filter((fid): fid is string => typeof fid === 'string'),
                  }))
                );

                const nestedFieldLabelMap = buildFieldLabelMap(formDsl);
                const nestedComponentMap = buildComponentMap(formDsl);

                const summaryProps = {
                  ...(component.props || {}),
                  modules: summaryModules,
                  fieldLabelMap: nestedFieldLabelMap,
                  componentMap: nestedComponentMap,
                };

                return (
                  <div
                    key={
                      component.id ??
                      component.fieldId ??
                      `${component.component}-${index}`
                    }
                    className={fieldWidthSpan(component.width)}
                  >
                    <Component {...component} props={summaryProps} />
                  </div>
                );
              }

              return (
                <div
                  key={
                    component.id ??
                    component.fieldId ??
                    `${component.component}-${index}`
                  }
                  className={fieldWidthSpan(component.width)}
                >
                  <Component {...component} />
                </div>
              );
            }
          );

          const moduleContent = (
            <div className="grid grid-cols-6 gap-x-4 gap-y-6 w-full">
              {componentsContent}
            </div>
          );

          if (isNestedFormOnlyModule) {
            return (
              <div
                key={module.id}
                className="grid grid-cols-6 gap-x-4 gap-y-6 w-full"
              >
                {componentsContent}
              </div>
            );
          }

          return (
            <CollapsibleModuleCard
              key={module.id}
              title={module.name}
              description={module.description}
              isExpanded={expandedModules.has(module.id)}
              onExpandedChange={(expanded) =>
                handleModuleExpandChange(module.id, expanded)
              }
            >
              {moduleContent}
            </CollapsibleModuleCard>
          );
        })}
      </NewIncorporationLayout>
    </FormContext.Provider>
  );
};
